import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { usePlaylistStore } from '@/store/playlist/store';
import {
  PlaylistVideoPlayer,
  PlaylistHeader,
  PlaylistProgressBar,
  EpisodeSidebar,
  EpisodeDrawer,
  PlaylistCompletionScreen,
  PlaylistFooter
} from '@/components/playlist/player';
import { Skeleton } from '@/components/ui/skeleton';
import { TemplateCleanPreview } from '@/components/playlist/preview/TemplateCleanPreview';
import { TemplatePandaPreview } from '@/components/playlist/preview/TemplatePandaPreview';
import { TemplatePrimeVideoPreview } from '@/components/playlist/preview/TemplatePrimeVideoPreview';
import { TemplateDarkMembersPreview } from '@/components/playlist/preview/TemplateDarkMembersPreview';
import { DarkMembersPlaylistView } from '@/components/playlist/preview/DarkMembersPlaylistView';
import type { PrimeVideoSettings } from '@/store/playlist/types';

interface PlaylistProgress {
  currentEpisode: number;
  watchedEpisodes: number[];
  lastWatched: string;
}

export default function PlaylistPlayer() {
  useLanguage();
  const { hash } = useParams();
  const getPlaylistByHash = usePlaylistStore((state) => state.getPlaylistByHash);
  const [playlist, setPlaylist] = useState(() => hash ? getPlaylistByHash(hash) : null);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [watchedEpisodes, setWatchedEpisodes] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedBonusId, setSelectedBonusId] = useState<string | null>(null);

  // Load progress from localStorage
  useEffect(() => {
    if (!hash) return;

    const savedProgress = localStorage.getItem(`playlist-progress-${hash}`);
    if (savedProgress) {
      try {
        const progress: PlaylistProgress = JSON.parse(savedProgress);
        setCurrentEpisodeIndex(progress.currentEpisode);
        setWatchedEpisodes(new Set(progress.watchedEpisodes));
      } catch (error) {
        console.error('Error loading playlist progress:', error);
      }
    }

    setIsLoading(false);
  }, [hash]);

  // Save progress to localStorage
  useEffect(() => {
    if (!hash) return;

    const progress: PlaylistProgress = {
      currentEpisode: currentEpisodeIndex,
      watchedEpisodes: Array.from(watchedEpisodes),
      lastWatched: new Date().toISOString()
    };

    localStorage.setItem(`playlist-progress-${hash}`, JSON.stringify(progress));
  }, [currentEpisodeIndex, watchedEpisodes, hash]);

  const handleVideoEnded = () => {
    // Mark current episode as watched
    setWatchedEpisodes(prev => new Set([...prev, currentEpisodeIndex]));

    // Check if there's a next episode
    if (playlist && currentEpisodeIndex < playlist.videos.length - 1) {
      // Auto-advance to next episode
      setTimeout(() => {
        setCurrentEpisodeIndex(prev => prev + 1);
      }, 500);
    } else {
      // Show completion screen
      setShowCompletion(true);
    }
  };

  const handleSelectEpisode = (index: number) => {
    setCurrentEpisodeIndex(index);
    setShowCompletion(false);
  };

  const handleRestart = () => {
    setCurrentEpisodeIndex(0);
    setWatchedEpisodes(new Set());
    setShowCompletion(false);
    if (hash) {
      localStorage.removeItem(`playlist-progress-${hash}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <Skeleton className="h-10 w-64" />
          </div>
        </div>
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="aspect-video w-full rounded-xl" />
        </div>
      </div>
    );
  }

  // Playlist not found or private
  if (!playlist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">{translateInterfaceText("Playlist não encontrada")}</h1>
          <p className="text-muted-foreground">{translateInterfaceText("A playlist que você está procurando não existe ou foi removida.")}</p>
        </div>
      </div>
    );
  }


  // Verificar se playlist tem conteúdo baseado no template
  const hasContent = (() => {
    // Para Dark Members, verificar dentro dos módulos e bônus
    if (playlist.template === 'dark-members' && playlist.darkMembersSettings) {
      const moduleLessons = playlist.darkMembersSettings.modules?.reduce(
        (acc, module) => acc + (module.lessons?.length ?? 0), 0
      ) ?? 0;
      
      const bonusLessons = playlist.darkMembersSettings.bonuses?.reduce(
        (acc, bonus) => acc + (bonus.lessons?.length ?? 0), 0
      ) ?? 0;
      
      return (moduleLessons + bonusLessons) > 0;
    }
    
    // Para outros templates, verificar array videos
    return (playlist.videos?.length ?? 0) > 0;
  })();

  // Empty playlist
  if (!hasContent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">{translateInterfaceText("Playlist vazia")}</h1>
          <p className="text-muted-foreground">{translateInterfaceText("Esta playlist ainda não possui vídeos.")}</p>
        </div>
      </div>
    );
  }

  const currentVideo = playlist.videos[currentEpisodeIndex];
  const autoPlayNext = currentEpisodeIndex > 0; // Auto-play for subsequent episodes

  // Renderizar template correto baseado na seleção
  const renderTemplate = () => {
    switch (playlist.template) {
      case 'clean':
        return (
          <TemplateCleanPreview
            videos={playlist.videos}
            playlistName={playlist.name}
            publicTitle={playlist.publicTitle || playlist.name}
            description={playlist.description}
            favicon={playlist.favicon}
          />
        );
      
      case 'panda':
        return (
          <TemplatePandaPreview
            videos={playlist.videos}
            playlistName={playlist.name}
            publicTitle={playlist.publicTitle || playlist.name}
            description={playlist.description}
            favicon={playlist.favicon}
          />
        );
      
      case 'netflix':
        return (
          <TemplatePrimeVideoPreview
            videos={playlist.videos}
            settings={playlist.primeVideoSettings}
          />
        );
      
      case 'dark-members':
        // Se tem módulo selecionado
        if (selectedModuleId) {
          const selectedModule = playlist.darkMembersSettings?.modules.find(
            m => m.id === selectedModuleId
          );
          if (selectedModule) {
            return (
              <DarkMembersPlaylistView
                contentType="module"
                contentId={selectedModule.id}
                title={selectedModule.title}
                lessons={selectedModule.lessons || []}
                thumbnailUrl={selectedModule.thumbnailUrl}
                description={playlist.description}
                onBack={() => setSelectedModuleId(null)}
              />
            );
          }
        }
        
        // Se tem bônus selecionado
        if (selectedBonusId) {
          const selectedBonus = playlist.darkMembersSettings?.bonuses.find(
            b => b.id === selectedBonusId
          );
          if (selectedBonus) {
            return (
              <DarkMembersPlaylistView
                contentType="bonus"
                contentId={selectedBonus.id}
                title={selectedBonus.title}
                lessons={selectedBonus.lessons || []}
                thumbnailUrl={selectedBonus.thumbnailUrl}
                description={playlist.description}
                onBack={() => setSelectedBonusId(null)}
              />
            );
          }
        }
        
        // Renderizar home (cards de módulos e bônus)
        return (
          <TemplateDarkMembersPreview
            settings={playlist.darkMembersSettings}
            onModuleClick={(moduleId) => setSelectedModuleId(moduleId)}
            onBonusClick={(bonusId) => setSelectedBonusId(bonusId)}
          />
        );
      
      default:
        // Fallback para layout atual
        return renderDefaultLayout();
    }
  };

  const renderDefaultLayout = () => {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <PlaylistHeader
          playlistName={playlist.name}
          headerTitle={playlist.headerTitle}
          favicon={playlist.favicon}
          creatorName={playlist.creatorName}
          customLogo={playlist.customLogo}
          externalButton={playlist.externalButton}
          appearanceSettings={playlist.appearanceSettings}
        />

        {/* Main Layout */}
        <div className="flex">
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="container mx-auto px-4 py-6 lg:py-8 max-w-6xl">
              {/* Video Player */}
              <PlaylistVideoPlayer
                videoUrl={`https://example.com/videos/${currentVideo.videoId}.mp4`}
                thumbnail={currentVideo.customThumbnail}
                onEnded={handleVideoEnded}
                autoPlay={autoPlayNext}
                theme={playlist.theme}
              />

              {/* Progress Bar (below video on mobile/tablet) */}
              <div className="mt-4 lg:mt-6">
                <PlaylistProgressBar
                  currentEpisode={currentEpisodeIndex}
                  totalEpisodes={playlist.videos.length}
                  watchedEpisodes={watchedEpisodes}
                  theme={playlist.theme}
                  episodeName={currentVideo.customName}
                />
              </div>

              {/* Episode Drawer (mobile only) */}
              <div className="mt-4">
                <EpisodeDrawer
                  videos={playlist.videos}
                  currentIndex={currentEpisodeIndex}
                  watchedEpisodes={watchedEpisodes}
                  onSelectEpisode={handleSelectEpisode}
                  theme={playlist.theme}
                />
              </div>
            </div>
          </main>

          {/* Episode Sidebar (desktop only) */}
          <EpisodeSidebar
            videos={playlist.videos}
            currentIndex={currentEpisodeIndex}
            watchedEpisodes={watchedEpisodes}
            onSelectEpisode={handleSelectEpisode}
            theme={playlist.theme}
            totalDuration={playlist.totalDuration}
          />
        </div>

        {/* Completion Screen */}
        {showCompletion && (
          <PlaylistCompletionScreen
            playlistName={playlist.name}
            onRestart={handleRestart}
            externalButton={playlist.externalButton}
            theme={playlist.theme}
          />
        )}

        {/* Footer */}
        <PlaylistFooter
          footerSettings={playlist.footerSettings}
          creatorName={playlist.creatorName}
        />
      </div>
    );
  };

  return renderTemplate();
}
